import { usePropertyTypes } from "@/hooks/usePropertyTypes";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { SearchContextProps } from "./search/types";

// Define the shape of the filters object passed to handleFilterChange
export interface FilterChangePayload {
  categories?: string[];
  types?: string[];
  locations?: string[]; // This will now be the unified location array
  priceRange?: [number, number];
  featured?: boolean;
}

// Use the imported SearchContextProps here
const SearchContext = createContext<SearchContextProps | undefined>(undefined);

export const SearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search_term") || ""
  );
  const [displayMode, setDisplayMode] = useState<"grid" | "list">("grid");
  const [isFiltersVisible, setIsFiltersVisible] = useState(true);
  const [sortBy, setSortBy] = useState(
    searchParams.get("sort_by") || "created_at"
  );

  const { data: propertyTypes = [] } = usePropertyTypes();

  // *** Original invalidateDuplicateQueries ***
  const invalidateDuplicateQueries = useCallback(() => {
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === "listings" && query.state.dataUpdateCount > 1,
    });
  }, [queryClient]);

  // *** Original handleSearch ***
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const newParams = new URLSearchParams(searchParams);
    if (searchQuery) {
      newParams.set("search_term", searchQuery);
    } else {
      newParams.delete("search_term");
    }
    newParams.set("page", "1");
    invalidateDuplicateQueries(); // Use the defined function
    setSearchParams(newParams, { replace: true });
  };

  // *** MODIFIED handleFilterChange ***
  const handleFilterChange = useCallback(
    (filters: FilterChangePayload) => {
      try {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("page", "1"); // Reset page

        // Helper function to set or delete param based on array
        const updateMultiSelectParam = (
          key: string,
          values: string[] | undefined
        ) => {
          if (values && values.length > 0) {
            newParams.set(key, values.join(","));
          } else {
            newParams.delete(key);
          }
        };

        updateMultiSelectParam("listing_type", filters.categories);
        updateMultiSelectParam("propertyType", filters.types);
        updateMultiSelectParam("location", filters.locations);

        // Price Range
        if (filters.priceRange) {
          const [min, max] = filters.priceRange;
          if (min != null && min > 0) {
            newParams.set("minPrice", min.toString());
          } else {
            newParams.delete("minPrice");
          }
          if (max != null && max < 50000000) {
            newParams.set("maxPrice", max.toString());
          } else {
            newParams.delete("maxPrice");
          }
        } else {
          newParams.delete("minPrice");
          newParams.delete("maxPrice");
        }

        // Featured
        if (filters.featured === true) {
          newParams.set("featured", "true");
        } else {
          newParams.delete("featured");
        }

        // Preserve search term
        const currentSearch = searchParams.get("search_term");
        if (currentSearch) {
          newParams.set("search_term", currentSearch);
        }

        // Preserve sorting
        const currentSortBy = searchParams.get("sort_by");
        const currentSortOrder = searchParams.get("sort_order");
        if (currentSortBy) {
          newParams.set("sort_by", currentSortBy);
        }
        if (currentSortOrder) {
          newParams.set("sort_order", currentSortOrder);
        }

        invalidateDuplicateQueries();
        setSearchParams(newParams, { replace: true });
      } catch (error) {
        console.error("Error updating filters:", error);
        toast.error("Failed to update filters");
      }
    },
    [searchParams, setSearchParams, invalidateDuplicateQueries]
  );

  // *** MODIFIED handleClearFilters ***
  const handleClearFilters = useCallback(() => {
    const newParams = new URLSearchParams(); // Start fresh
    newParams.set("page", "1"); // Reset page

    // Restore only non-filter params (search, sort)
    const searchTerm = searchParams.get("search_term");
    const sortByParam = searchParams.get("sort_by");
    const sortOrderParam = searchParams.get("sort_order");
    if (searchTerm) newParams.set("search_term", searchTerm);
    if (sortByParam) newParams.set("sort_by", sortByParam);
    if (sortOrderParam) newParams.set("sort_order", sortOrderParam);

    invalidateDuplicateQueries();
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, invalidateDuplicateQueries]);

  // *** Original handleSortChange ***
  const handleSortChange = useCallback(
    (sortValue: string) => {
      setSortBy(sortValue);
      const newParams = new URLSearchParams(searchParams);
      switch (sortValue) {
        case "latest":
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "desc");
          break;
        case "oldest":
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "asc");
          break;
        case "price_high":
          newParams.set("sort_by", "price");
          newParams.set("sort_order", "desc");
          break;
        case "price_low":
          newParams.set("sort_by", "price");
          newParams.set("sort_order", "asc");
          break;
        default:
          newParams.set("sort_by", "created_at");
          newParams.set("sort_order", "desc");
      }
      newParams.set("page", "1");
      invalidateDuplicateQueries();
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams, invalidateDuplicateQueries, setSortBy]
  );

  // *** Original handleFloatingFilterChange (Needs Review/Removal) ***
  const handleFloatingFilterChange = useCallback(
    (type: string, value: string) => {
      console.warn(
        "handleFloatingFilterChange needs review/removal for multi-select logic."
      );
    },
    []
  );

  // Context Value
  const value: SearchContextProps = {
    searchQuery,
    setSearchQuery,
    displayMode,
    setDisplayMode,
    isFiltersVisible,
    setIsFiltersVisible,
    sortBy,
    setSortBy,
    // Use the handlers defined in this component scope
    handleSearch,
    handleFilterChange,
    handleFloatingFilterChange,
    handleClearFilters,
    handleSortChange,
    invalidateDuplicateQueries,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};
